import { useState, useSyncExternalStore } from 'react';
import { useLang } from '../i18n/lang';
import { LinkedText } from './LinkedText';
import { preferredTranslation, rememberTranslation, subscribeTranslation } from './chosenTranslation';
import { chooseRendering, renderingsOf } from './passages';
import type { NameSpan } from '@versesverse/core';
import type { Passage } from './passages';

/*
 * ONE VERSE, IN THE ENGLISH THIS READER CHOSE — AND THE OTHERS, ONE CLICK AWAY.
 *
 * WHAT THIS REPLACES. Every rendering a verse had was printed, always, stacked: the main text
 * with no caption at all, then the second translation, then the third, each with its translator's
 * name. Two problems, and the second is worse than it looks.
 *
 * It does not scale, and scaling is the point. The aim is as many translations as can be
 * licensed, because where two translators differ the difference IS the evidence — Isaiah 7:14 is
 * "a virgin" in the King James and "the young woman" in the JPS, and that one word is most of
 * what two traditions have said to each other. Ten renderings stacked under every verse is not
 * ten times the evidence; it is a wall of text nobody reads.
 *
 * And the anonymous one was the main one. `altName` and `alt2Name` were printed, so a reader
 * could tell whose words the ALTERNATIVES were, while the text they were actually reading had no
 * name on it. A caption is a claim about who wrote the words above it, and the words most people
 * read carried none.
 *
 * THE CAPTION IS THE CONTROL, which is why this adds no chrome. The name has to be on screen
 * anyway; making it the button means the choice costs no toolbar, no menu icon, and no second
 * place to look. Closed, a reader sees one translation and whose it is. Open, they see all of
 * them and can compare — which is the old behaviour, now on request instead of always.
 *
 * CHOOSING ONE CHOOSES IT EVERYWHERE, ACROSS NODES. The preference is one value for the whole
 * library, matched BY NAME, so picking the King James here reaches every text that has it —
 * the Hebrew Bible and the New Testament both. A passage without that translation falls back to
 * its own first rendering, captioned with what it actually is: the JPS covers the Hebrew Bible
 * and nothing else, and a reader who prefers it must still be able to read the Quran.
 *
 * NOTHING IS TRANSLATED, SUBSTITUTED OR FALLEN BACK TO. A rendering with no text is not offered,
 * and a remembered choice the build no longer has falls back to the FIRST with its own caption —
 * never to another translator's words under the chosen name. See `chooseRendering`.
 */
export interface PassageTextProps {
  passage: Passage;
  onJump?: (id: string) => void;
}

export function PassageText({ passage, onJump }: PassageTextProps): React.JSX.Element {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  /*
   * SUBSCRIBED, NOT COPIED INTO LOCAL STATE, and the difference is visible on screen.
   *
   * A node's panel may show thirty verses and a reader may have a second node open beside it.
   * With the preference in each component's own state, picking a translation on one passage moves
   * that passage and leaves the other twenty-nine where they were — the same text in two Englishes
   * on one screen, which is worse than never offering the choice. Subscribing means one pick moves
   * every passage on the page, and the next node opens already in the chosen translation.
   */
  const preferred = useSyncExternalStore(subscribeTranslation, preferredTranslation, () => undefined);

  const renderings = renderingsOf(passage);
  const showing = chooseRendering(renderings, preferred);

  /* No rendering at all means the range has not arrived yet — not that the verse is empty. The
     distinction matters: one is a loading state and the other would be a claim about scripture. */
  if (!showing) return <p className="verse-missing">{t('verses.notLoaded')}</p>;

  /* By name, because that is the identity that reaches other texts — see `chooseRendering`. */
  const pick = (name: string): void => {
    rememberTranslation(name);
    setOpen(false);
  };

  return (
    <>
      {/* `lang` and `dir` because the renderings stopped being English on 2026-08-16. Two of the
          four new ones read right to left, and Arabic laid out left to right is not a subtle
          defect; `lang` is also what lets a screen reader pronounce any of them at all. Undefined
          for the English renderings, which is what they were and what most will go on being. */}
      <p className="verse-text" lang={showing.lang} dir={showing.rtl ? 'rtl' : undefined}>
        <LinkedText text={showing.text} links={showing.links} onJump={onJump} />
      </p>
      {showing.name && (
        renderings.length > 1
          ? (
            <button
              type="button"
              className="verse-alt-name verse-translation-pick"
              aria-expanded={open}
              onClick={() => setOpen(was => !was)}
            >
              {showing.name}
              {/* Says what opening will do, for a reader who cannot see the arrow and for one who
                  has no idea this text has more than one English. */}
              <span className="verse-translation-more"> · {t('verses.otherTranslations', { n: String(renderings.length - 1) })}</span>
            </button>
          )
          /* One rendering is not a choice, so it is a caption and not a button — a control that
             does nothing is worse than no control. */
          : <p className="verse-alt-name">{showing.name}</p>
      )}
      {open && (
        <ul className="verse-translations">
          {renderings.map(r => (
            <li key={r.id} className={r.id === showing.id ? 'verse-translation is-chosen' : 'verse-translation'}>
              <button
                type="button"
                className="verse-translation-choose"
                aria-current={r.id === showing.id ? 'true' : undefined}
                onClick={() => pick(r.name)}
              >
                {r.name}
              </button>
              {/* Every rendering shown in full while open — this is the comparison, and a preview
                  that truncated the one word two traditions differ on would defeat it. */}
              <p className="verse-alt" lang={r.lang} dir={r.rtl ? 'rtl' : undefined}>
                <LinkedText text={r.text} links={r.links} onJump={onJump} />
              </p>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Re-exported so `VersesList` need not import the span type separately. */
export type { NameSpan };
